import { GameConfig } from "../config";
import { InvaderKind, InvaderSeed, BunkerSeed } from "../types";
import { textToMask } from "../text/textMask";
import {
  GLYPH_HEIGHT,
  GLYPH_WIDTH,
  LETTER_SPACING,
  glyphFor,
  lineWidthInCols,
} from "../text/pixelFont";

const defaultKindForRow = (row: number): InvaderKind =>
  row === 0 ? "top" : row < 3 ? "mid" : "bottom";

export interface TextSwarmOptions {
  lines: string[];
  /** Horizontal distance between cell origins, in px. */
  cellPitch: number;
  /** Vertical distance between cell origins. Defaults to `cellPitch`. */
  rowPitch?: number;
  /** Top edge of the first rank. */
  originY: number;
  kindForRow?: (row: number) => InvaderKind;
}

/** Spells `lines` out in invaders, centred horizontally on the canvas. */
export function textSwarmLayout(options: TextSwarmOptions) {
  const mask = textToMask(options.lines);
  const rowPitch = options.rowPitch ?? options.cellPitch;
  const kindForRow = options.kindForRow ?? defaultKindForRow;

  return (level: number, cfg: GameConfig): InvaderSeed[] => {
    const cols = mask[0].length;
    const width = (cols - 1) * options.cellPitch + cfg.swarm.invaderWidth;
    const originX = (cfg.canvas.width - width) / 2;
    const drop = Math.min(level - 1, 4) * cfg.swarm.levelDrop;
    const seeds: InvaderSeed[] = [];

    for (let r = 0; r < mask.length; r++) {
      for (let c = 0; c < cols; c++) {
        if (!mask[r][c]) continue;
        seeds.push({
          x: originX + c * options.cellPitch,
          y: options.originY + drop + r * rowPitch,
          kind: kindForRow(r),
        });
      }
    }
    return seeds;
  };
}

export interface TextBunkerOptions {
  lines: string[];
  /** Size of one glyph pixel, in px. Each letter becomes a 5x7 block grid. */
  blockSize: number;
  originY: number;
  /** Vertical gap between stacked lines. Defaults to three blocks. */
  lineGap?: number;
}

/**
 * One destructible bunker per letter, so erosion reads letter by letter and
 * each `Bunker.hit` scan stays short.
 */
export function textBunkerLayout(options: TextBunkerOptions) {
  return (cfg: GameConfig): BunkerSeed[] => {
    const { lines, blockSize, originY } = options;
    const lineGap = options.lineGap ?? blockSize * 3;
    const widths = lines.map(lineWidthInCols);
    const cols = Math.max(...widths);
    const originX = (cfg.canvas.width - cols * blockSize) / 2;
    const seeds: BunkerSeed[] = [];

    lines.forEach((line, lineIndex) => {
      const y = originY + lineIndex * (GLYPH_HEIGHT * blockSize + lineGap);
      let col = Math.floor((cols - widths[lineIndex]) / 2);

      for (const character of line) {
        if (character !== " ") {
          seeds.push({
            x: originX + col * blockSize,
            y,
            shape: glyphFor(character),
            blockSize,
          });
        }
        col += GLYPH_WIDTH + LETTER_SPACING;
      }
    });

    return seeds;
  };
}
